import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { getAmounts, intToDec } from "../../utils";
import { useState } from "react";
import Api from "../../Api";
import { Quote, QuoteNote } from "../../types";

const Details = ({
  onClose,
  installation,
  clientName,
  sellerName,
  markComplete,
  onAddNote,
}: {
  onClose: () => void;
  installation: Quote;
  clientName: string;
  sellerName: string;
  markComplete: () => void;
  onAddNote: () => void;
}) => {
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const addNote = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    Api.installationsAddNote(installation.uuid, newNote).then(() => [
      setSaving(false),
      onAddNote(),
      setNewNote(""),
    ]);
  };

  const closeAndComplete = () => {
    if (window.confirm("Deseja marcar a instalação como finalizada?")) {
      onClose();
      markComplete();
    }
  };

  const { finalSale, finalRent, fee } = getAmounts(installation);

  return (
    <div className="absolute bottom-0 right-0 h-[100dvh] w-full z-10 md:w-[500px] md:border-l bg-white overflow-hidden shadow-md pt-[57px]">
      <div className="flex items-center border-b">
        <div className="px-4 font-semibold flex-1">
          <span className="py-1 px-2 bg-gray-100 text-gray-600 rounded-full font-bold">
            {installation.number}
          </span>
        </div>

        <button
          className="py-3 px-4 hover:bg-gray-100 border-l"
          onClick={onClose}
        >
          <FontAwesomeIcon icon="xmark" />
        </button>
      </div>

      <div className="flex flex-col h-full -mt-[49px] pt-[49px]">
        <div className="flex flex-col gap-2 p-4">
          <div className="text-lg font-bold">{clientName}</div>

          <div className="flex items-center gap-2 text-sm">
            <FontAwesomeIcon icon="clock" className="text-gray-500" />{" "}
            Autorizado em{" "}
            <span className="font-bold">
              {moment(installation.authorizationDate).format("DD/MM/YY")}
            </span>{" "}
            às{" "}
            <span className="font-bold">
              {moment(installation.authorizationDate).format("HH:mm")}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <FontAwesomeIcon icon="user" className="text-gray-500" />
            {sellerName}
          </div>

          <div className="flex gap-2">
            {finalSale > 0 && (
              <div className="bg-cyan-100 text-cyan-800 rounded text-xs px-1.5 py-0.5 tracking-tighter">
                Venda <span className="font-bold">{intToDec(finalSale)}</span>
              </div>
            )}

            {finalRent > 0 && (
              <div className="bg-lime-100 text-lime-800 rounded text-xs px-1.5 py-0.5 tracking-tighter">
                Mensal <span className="font-bold">{intToDec(finalRent)}</span>
              </div>
            )}

            {fee > 0 && (
              <div className="bg-violet-100 text-violet-800 rounded text-xs px-1.5 py-0.5 tracking-tighter">
                Taxa <span className="font-bold">{intToDec(fee)}</span>
              </div>
            )}
          </div>
        </div>

        {installation.notes.length > 0 && <Notes notes={installation.notes} />}

        {installation.notes.length === 0 && <div className="flex-1" />}

        <form className="py-4 px-8 border-t" onSubmit={addNote}>
          <input
            className="border rounded-full w-full text-sm py-1 px-3 disabled:text-gray-500"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            disabled={saving}
            placeholder="Anotação..."
          />
        </form>

        <div className="border-t p-4 text-sm text-center">
          <button
            className="bg-yellow-100 text-yellow-600 px-4 py-2 rounded font-bold"
            onClick={closeAndComplete}
          >
            <FontAwesomeIcon className="mr-2" icon="gears" /> Instalação
            finalizada
          </button>
        </div>
      </div>
    </div>
  );
};

export default Details;

const Notes = ({ notes }: { notes: QuoteNote[] }) => {
  return (
    <div className="-mx-4 py-4 px-12 border-t flex flex-col gap-2 overflow-scroll h-full flex-1">
      {notes.map((note, i) => (
        <div key={i} className="text-sm border rounded-lg py-1 px-2">
          <div className="text-xs flex text-gray-500">
            <span className="font-bold mr-1">{note.user}</span>
            <span className="font-light flex-1">
              {moment(note.date).format("DD/MM/YY")}
            </span>
          </div>

          <div>{note.text}</div>
        </div>
      ))}
    </div>
  );
};
